import { useEffect, useState } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { PincodeAreaModel } from "../../models/cust-address/cust-address-model";
import { Toast } from "primereact/toast";
import UserAddressService from "../../services/useraddress/useraddress.service";

interface DataTableProps {
  data: any[];
  NoofRecords: number;
  pageNumber: number;
  pageSize: number;
  searchTerm: any;
  onPageChange: any;
  toastRef: React.RefObject<Toast>;
  refreshData: () => void;
}

const AreaPincodeDataTable: React.FC<DataTableProps> = (props) => {
  const [selectedArea, setSelectedArea] = useState<PincodeAreaModel | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {}, [props.data]);

  const handleRowEdit = async (e: DataTableRowEditCompleteEvent) => {
    const { newData } = e;
    try {
      const updateData = {
        areaId: newData.areaId,
        areaName: newData.areaName,
        pincode: newData.pinCode,
      };

      const response = await UserAddressService.updateAreaPincode(updateData);
      
      if (response?.isSuccess) {
        props.toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Area/Pincode updated successfully',
          life: 3000
        });
        props.refreshData();
      } else {
        props.toastRef.current?.show({
          severity: 'warn',
          summary: 'Area Name/ Pincode can not be Empty',
          detail: response?.message || 'Update failed',
          life: 3000
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      props.toastRef.current?.show({
        severity: 'warn',
        summary: 'Area Name/ Pincode can not be Empty',
        detail: 'Update failed. Please try again.',
        life: 3000
      });
    }
  };

  const pincodeEditor = (options: ColumnEditorOptions) => (
    <InputText
      type="text"
      value={options.value}
      maxLength={6}
      style={{ width: "95%", border: "1px solid #090" }}
      onChange={(e) => {
        const value = e.target.value.replace(/\D/g, '');
        options.editorCallback!(value);
      }}
    />
  );

  const areaEditor = (options: ColumnEditorOptions) => (
    <InputText
      type="text"
      value={options.value}
      style={{ width: "95%", border: "1px solid #090" }}
      onChange={(e) => options.editorCallback!(e.target.value)}
    />
  );

  const confirmDelete = (rowData: PincodeAreaModel) => {
    setSelectedArea(rowData);
    setShowDeleteDialog(true);
  };

  const deleteAreaPincode = async () => {
    if (!selectedArea?.areaId) return;

    try {
      const response = await UserAddressService.deleteAreaPincode(selectedArea.areaId);
      
      if (response.isSuccess) {
        props.toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: response.message,
          life: 3000
        });
        props.refreshData();
      } else {
        props.toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.message,
          life: 3000
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      props.toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Delete failed. Please try again.',
        life: 3000
      });
    } finally {
      setShowDeleteDialog(false);
      setSelectedArea(null);
    }
  };

  const deleteTemplate = (rowData: PincodeAreaModel) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text"
      style={{ color: 'gray' }}
      onClick={() => confirmDelete(rowData)}
    />
  );

  return (
    <div className="col-12">
      <div className="card border-noround p-2">
        <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          totalRecords={props.NoofRecords}
          lazy
          editMode="row"
          dataKey="areaName"
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate={`{first} to {last} of ${props.NoofRecords}`}
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          onRowEditComplete={handleRowEdit}
        >
          <Column field="pinCode" header="Pincode" editor={pincodeEditor} style={{ width: "20%" }} />
          <Column field="areaName" header="Area Name" editor={areaEditor} style={{ width: "25%" }} />
          <Column field="subDistrictName" header="Sub District" style={{ width: "25%" }} />
          <Column rowEditor header="Edit" headerStyle={{ width: "5%" }} bodyStyle={{ textAlign: 'center' }} />
          <Column body={deleteTemplate} header="Delete" style={{ width: "5%" }} bodyStyle={{ textAlign: 'center' }} />
        </DataTable>
      </div>

      <Dialog
        visible={showDeleteDialog}
        style={{ width: '450px' }}
        header="Confirm Deletion"
        modal
        footer={
          <div>
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              className="p-button-text" 
              onClick={() => setShowDeleteDialog(false)} 
            />
            <Button 
              label="Delete" 
              icon="pi pi-trash" 
              className="p-button-danger" 
              onClick={deleteAreaPincode} 
            />
          </div>
        }
        onHide={() => setShowDeleteDialog(false)}
      >
        <div className="confirmation-content">
          {selectedArea && (
            <span>
              Are you sure you want to delete <b>{selectedArea.areaName}</b> (Pincode: {selectedArea.pinCode})?
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default AreaPincodeDataTable;
